import React from "react";
import { useAuth } from "../../context/authContext";
import { useState } from "react";
import { 
    Typography, 
    Box, 
    Button, 
    TextField,
    CircularProgress,
    Alert
} from "@mui/material";
import Swal from "sweetalert2";
import { ALERT_MESSAGES, BUTTON, LABELS, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import { sendMachineNotifyEmail } from "../../services/machineNotifyFormService";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import { useSite } from "../../context/siteContext";
import PhoneInput from "../PhoneInput/PhoneInput";
import { resolvePhoneState } from "../../utilities/phoneUtils";

const MachineNotifyForm = ({ open, setOpen, machine_number, machine_name }) => {
    const modalWidth = "500px";

    const { user } = useAuth();
    const { selectedSite, selectedSiteData } = useSite();
    const { t } = useTranslation();

    const getInitialPhoneState = () =>
        resolvePhoneState({
            phoneValue: user?.user_phone || "",
            countryLabel: user?.country || user?.user_meta?.country || "",
            phoneCountryIso2: user?.phone_country_iso2 || user?.user_meta?.phone_country_iso2 || "",
            phoneCountryCode: user?.phone_country_code || user?.user_meta?.phone_country_code || "",
        });

    // FORM DATA STATE
    const initialPhoneState = getInitialPhoneState();
    const [formData, setFormData] = useState({
        email: user?.user_email || "",
        fullname: user?.user_display_name || "",
        phone: initialPhoneState.phoneValue,
        phone_country_iso2: initialPhoneState.meta.phone_country_iso2,
        phone_country_code: initialPhoneState.meta.phone_country_code,
        machine: `${machine_number} - ${machine_name}`,
        subject: "Machine Sold Notification",
        message: "",
        site_number: selectedSite,
        site_id: selectedSiteData?.site_id || null,
    });

    // ERROR STATES
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showAlert, setShowAlert] = useState(false);

    // VALIDATION FUNCTIONS
    const validateEmail = (email) => {
        if (!email) return t('ALERT_MESSAGES.EMAIL_IS_REQUIRED');
        if (!email.trim()) return t('GENERAL.EMAIL_CANNOT_BE_EMPTY');
        
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
            return t('GENERAL.PLEASE_ENTER_A_VALID_EMAIL_ADDRESS');
        }
        return "";
    };

    const validateFullName = (name) => {
        if (!name) return t('GENERAL.FULL_NAME_IS_REQUIRED');
        if (!name.trim()) return t('GENERAL.FULL_NAME_CANNOT_BE_EMPTY');
        if (name.trim().length < 2) return t('GENERAL.FULL_NAME_MUST_BE_AT_LEAST_2_CHARACTERS');
        if (name.trim().length > 100) return t('GENERAL.FULL_NAME_IS_TOO_LONG');
        
        // Check if name contains at least one letter
        if (!/[a-zA-Z]/.test(name)) return t('GENERAL.FULL_NAME_MUST_CONTAIN_LETTERS');
        
        return "";
    };

    const validatePhone = (phone) => {
        if (!phone) return t('GENERAL.PHONE_NUMBER_IS_REQUIRED');
        if (!phone.trim()) return t('GENERAL.PHONE_NUMBER_CANNOT_BE_EMPTY');
        
        // Remove all spaces, dashes, parentheses for validation
        const cleanPhone = phone.replace(/[\s\-\(\)\+]/g, '');
        
        if (cleanPhone.length < 7) return t('GENERAL.PHONE_NUMBER_IS_TOO_SHORT');
        if (cleanPhone.length > 15) return t('GENERAL.PHONE_NUMBER_IS_TOO_LONG');
        if (!/^\d+$/.test(cleanPhone)) return t('GENERAL.PHONE_NUMBER_CAN_ONLY_CONTAIN_DIGITS_SPACES_DASHES_PARENTHESES_AND');
        
        return "";
    };

    const validateMessage = (message) => {
        if (!message) return t('UI_TEXT.MESSAGE_IS_REQUIRED');
        if (!message.trim()) return t('GENERAL.MESSAGE_CANNOT_BE_EMPTY');
        if (message.trim().length < 10) return t('GENERAL.MESSAGE_MUST_BE_AT_LEAST_10_CHARACTERS');
        if (message.trim().length > 1000) return t('GENERAL.MESSAGE_IS_TOO_LONG_MAX_1000_CHARACTERS');
        return "";
    };

    const validateSubject = (subject) => {
        if (!subject) return t('UI_TEXT.SUBJECT_IS_REQUIRED');
        if (!subject.trim()) return t('GENERAL.SUBJECT_CANNOT_BE_EMPTY');
        if (subject.trim().length < 3) return t('GENERAL.SUBJECT_MUST_BE_AT_LEAST_3_CHARACTERS');
        return "";
    };

    // COMPREHENSIVE FORM VALIDATION
    const validateForm = () => {
        const newErrors = {};

        // Validate each field
        newErrors.email = validateEmail(formData.email);
        newErrors.fullname = validateFullName(formData.fullname);
        newErrors.phone = validatePhone(formData.phone);
        newErrors.subject = validateSubject(formData.subject);
        newErrors.message = validateMessage(formData.message);

        // Remove empty error messages
        Object.keys(newErrors).forEach(key => {
            if (!newErrors[key]) {
                delete newErrors[key];
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // REAL-TIME VALIDATION ON FIELD BLUR
    const handleFieldBlur = (fieldName, value) => {
        let fieldError = "";
        
        switch (fieldName) {
            case "email":
                fieldError = validateEmail(value);
                break;
            case "fullname":
                fieldError = validateFullName(value);
                break;
            case "phone":
                fieldError = validatePhone(value);
                break;
            case "subject":
                fieldError = validateSubject(value);
                break;
            case "message":
                fieldError = validateMessage(value);
                break;
            default:
                break;
        }

        setErrors(prev => ({
            ...prev,
            [fieldName]: fieldError
        }));
    };

    const handleCloseDialog = () => {
        // Reset form and errors when closing
        setFormData({
            email: user?.user_email || "",
            fullname: user?.user_display_name || "",
            phone: getInitialPhoneState().phoneValue,
            phone_country_iso2: getInitialPhoneState().meta.phone_country_iso2,
            phone_country_code: getInitialPhoneState().meta.phone_country_code,
            machine: `${machine_number} - ${machine_name}`,
            subject: "Machine Sold Notification",
            message: "",
            site_number: selectedSite,
            site_id: selectedSiteData?.site_id || null,
        });
        setErrors({});
        setShowAlert(false);
        setIsSubmitting(false);
        setOpen(false);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        let next = value;
        if (name === 'phone') {
            next = String(value || '').replace(/\D/g, '').slice(0, 10);
        }
        setFormData((prev) => ({ ...prev, [name]: next }));
        
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ""
            }));
        }
        
        // Hide alert when user makes changes
        if (showAlert) {
            setShowAlert(false);
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        
        // Prevent double submission
        if (isSubmitting) return;

        // Validate form
        if (!validateForm()) {
            setShowAlert(true);
            return;
        }

        setIsSubmitting(true);

        // Prepare request body with trimmed values
        const req_body = {
            name: formData.fullname.trim(),
            email: formData.email.trim().toLowerCase(),
            phone: formData.phone.trim(),
            subject: formData.subject.trim(),
            message: formData.message.trim(),
            machine: formData.machine,
            site_number: selectedSite,
            site_id: selectedSiteData?.site_id || null,
        };

        try {
            const response = await sendMachineNotifyEmail(req_body);
            if (response.status) {
                // Reset form
                setFormData({
                    email: user?.user_email || "",
                    fullname: user?.user_display_name || "",
                    phone: getInitialPhoneState().phoneValue,
                    phone_country_iso2: getInitialPhoneState().meta.phone_country_iso2,
                    phone_country_code: getInitialPhoneState().meta.phone_country_code,
                    machine: `${machine_number} - ${machine_name}`,
                    subject: "Machine Sold Notification",
                    message: "",
                });
                setErrors({});
                setShowAlert(false);
                setOpen(false);
                
                Swal.fire({
                    icon: "success",
                    title: t('ALERT_MESSAGES.SUCCESS'),
                    text: t('ALERT_MESSAGES.CUSTOMER_CARE_CONTACT_SHORTLY'),
                });
            } else {
                // Handle API error response
                Swal.fire({
                    icon: "error",
                    title: t('ALERT_MESSAGES.ERROR'),
                    text: response.data.message || "Failed to send notification. Please try again.",
                });
            }
        } catch (error) {
            console.error("Form submission error:", error);
            
            // Show user-friendly error message
            Swal.fire({
                icon: "error",
                title: t('ALERT_MESSAGES.ERROR'),
                text: error.response?.data?.message || "Failed to send notification. Please check your connection and try again.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const formContent = (
        <Box sx={{ width: '100%' }}>
            {showAlert && Object.keys(errors).length > 0 && (
                <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                        {t('GENERAL.PLEASE_FIX_THE_FOLLOWING_ERRORS')}
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: "20px" }}>
                        {Object.values(errors).map((error, index) => (
                            <li key={index}>
                                <Typography variant="body2">{error}</Typography>
                            </li>
                        ))}
                    </ul>
                </Alert>
            )}
            <form onSubmit={handleFormSubmit} style={{ width: "100%" }}>
                        {/* EMAIL FIELD */}
                        <TextField
                            fullWidth
                            size="small"
                            label={t('TABLE_HEADERS.EMAIL')}
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleFormChange}
                            onBlur={(e) => handleFieldBlur("email", e.target.value)}
                            error={!!errors.email}
                            helperText={errors.email}
                            disabled={isSubmitting}
                            sx={{ mb: 2, mt: 2 }}
                            required
                        />

                        {/* FULL NAME FIELD */}
                        <TextField
                            fullWidth
                            size="small"
                            label={`${t('LABELS.FULL_NAME')}`}
                            name="fullname"
                            value={formData.fullname}
                            onChange={handleFormChange}
                            onBlur={(e) => handleFieldBlur("fullname", e.target.value)}
                            error={!!errors.fullname}
                            helperText={errors.fullname}
                            disabled={isSubmitting}
                            sx={{ mb: 2 }}
                            required
                        />

                        {/* PHONE FIELD */}
                        <Box sx={{ mb: 2 }}>
                            <PhoneInput
                                value={formData.phone}
                                onChange={(value) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        phone: value,
                                    }))
                                }
                                countryIso2={formData.phone_country_iso2}
                                countryCode={formData.phone_country_code}
                                allowCountryChange={false}
                                onCountryChange={() => {}}
                                label={`${t('LABELS.PHONE')}`}
                                disabled={isSubmitting}
                                error={!!errors.phone}
                                helperText={errors.phone}
                            />
                        </Box>

                        {/* MACHINE FIELD (READ-ONLY) */}
                        <TextField
                            fullWidth
                            size="small"
                            label={t('LABELS.MACHINE_FORM')}
                            name="machine"
                            value={formData.machine}
                            disabled={true} // Machine info is not editable
                            sx={{ mb: 2 }}
                        />

                        {/* SUBJECT FIELD */}
                        <TextField
                            fullWidth
                            size="small"
                            label={`${t('LABELS.SUBJECT')}`}
                            name="subject"
                            value={formData.subject}
                            onChange={handleFormChange}
                            onBlur={(e) => handleFieldBlur("subject", e.target.value)}
                            error={!!errors.subject}
                            helperText={errors.subject}
                            disabled={isSubmitting}
                            sx={{ mb: 2 }}
                            required
                        />

                        {/* MESSAGE FIELD */}
                        <TextField
                            fullWidth
                            multiline
                            size="small"
                            rows={4}
                            label={`${t('LABELS.MESSAGE')}`}
                            name="message"
                            value={formData.message}
                            onChange={handleFormChange}
                            onBlur={(e) => handleFieldBlur("message", e.target.value)}
                            error={!!errors.message}
                            helperText={errors.message || `${formData.message.length}/1000 ${t('ALERT_MESSAGES.CHARACTERS_COUNT')}`}
                            disabled={isSubmitting}
                            sx={{ mb: 2 }}
                            inputProps={{ maxLength: 1000 }}
                            placeholder={t('GENERAL.PLEASE_DESCRIBE_YOUR_INQUIRY_ABOUT_THE_MACHINE')}
                            required
                        />

                        {/* ACTION BUTTONS */}
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "center",
                                alignItems: "center",
                                mb: 2,
                                mt: 1,
                                width: "100%",
                                columnGap: "15px",
                            }}>
                            <Button 
                                type="submit" 
                                variant="contained" 
                                color="primary"
                                disabled={isSubmitting}
                                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                                sx={{ minWidth: "120px" }}
                            >
                                {isSubmitting ? "Submitting..." : t('BUTTON.SUBMIT')}
                            </Button>
                            <Button
                                variant="outlined"
                                disabled={isSubmitting}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCloseDialog();
                                }}>
                                {t('BUTTON.CLOSE')}
                            </Button>
                        </Box>
            </form>
        </Box>
    );

    return (
        <ModalWrapper
            open={open}
            setOpen={setOpen}
            onClose={handleCloseDialog}
            title={t('UI_TEXT.MACHINE_SELL_NOTIFY')}
            subtitle={t('UI_TEXT.CUSTOMER_CARE_FOLLOW_UP_SUPPORT')}
            content={formContent}
            width={modalWidth}
        />
    );
};

export default MachineNotifyForm;
